/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable, NgZone} from '@angular/core';
import {EMPTY, firstValueFrom, from, Observable, switchMap} from 'rxjs';

import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {AgentOrderConfirmModalComponent} from './agent-order-confirm-modal.component';
import {OrderApiService} from './order-api.service';

/**
 * An AI agent booking may change and submit every proposed line item in an order that an agent booking created,
 * including line items that people put there. This service tells people when an order is such an order, and asks before
 * a person puts a line item into one. On a network without agent booking no agent session can authenticate, so every
 * answer is no and no order is read.
 */
@Injectable({providedIn: 'root'})
export class AgentBookingOrdersService {
  constructor(
    private readonly localNetworkProfile: LocalNetworkProfile,
    private readonly orderApi: OrderApiService,
    private readonly angularModalService: AngularModalService,
    private readonly ngZone: NgZone
  ) {
  }

  /** Whether an agent may change proposed line items in this order, as the API returned it. */
  isAgentOrder(order: any): boolean {
    return this.enabled() && !!order?.agentBooking;
  }

  /** Reads the order. Null means that the order could not be read, so it is not known whether an agent booked it. */
  lookUp(orderId: string | null | undefined): Promise<boolean | null> {
    if (!orderId || !this.enabled()) {
      return Promise.resolve(false);
    }
    return firstValueFrom(this.orderApi.get(orderId)).then((order) => !!order?.agentBooking, () => null);
  }

  /**
   * Resolves true when a line item may go into the order: an agent did not book the order, or the person confirmed.
   * The person is also asked when the order cannot be read.
   */
  confirmPutInto(orderId: string | null | undefined): Promise<boolean> {
    return this.lookUp(orderId).then((agentOrder) => agentOrder === false ? true : this.confirm(agentOrder === true));
  }

  /**
   * Runs a line item save that puts the line item into orderId. When the saved line item is in another order, or in none,
   * and an agent may have booked orderId, the save waits for the person to confirm. Declining saves nothing.
   */
  confirmSave<T>(orderId: string | null | undefined, savedOrderId: string | null | undefined, save: () => Observable<T>): Observable<T> {
    if (!orderId || orderId === savedOrderId) {
      return save();
    }
    return from(this.confirmPutInto(orderId)).pipe(switchMap((confirmed) => confirmed ? save() : EMPTY));
  }

  /** Asks the person to confirm. Pass false when it is not known whether an agent booked the order. */
  confirm(known = true): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      let settled = false;
      const settle = (confirmed: boolean) => {
        if (!settled) {
          settled = true;
          resolve(confirmed);
        }
      };
      this.ngZone.run(() => {
        const modalRef = this.angularModalService.show(AgentOrderConfirmModalComponent, {
          backdrop: 'static',
          ignoreBackdropClick: true,
          animated: false,
          initialState: {
            known,
            onConfirm: () => settle(true),
            onCancel: () => settle(false)
          }
        });
        // Escape and any other dismissal leave the line item where it was.
        modalRef.onHidden?.subscribe(() => settle(false));
      });
    });
  }

  private enabled(): boolean {
    return this.localNetworkProfile.hasCapability('AGENT_BOOKING');
  }
}
